import { api, buildQueryParams, URLs } from '@/libs/api'
import type {
  ApiResponse,
  CursorPaginatedResponse,
  Store,
  StoreData,
  StoreListParams,
  Stores,
  StoresData,
} from '@/libs/interfaces'
import { IS_MOCK_MODE } from '@/libs/mocks/config'
import { storesApiMock } from '@/libs/mocks/handlers/stores'

const storesApiReal = {
  create: async (payload: Partial<Stores>): Promise<Stores> => {
    return api
      .post<ApiResponse<Stores>>(URLs.Stores.create(payload.orgID || ''), payload)
      .then((response) => response.data.data)
  },
  getStores: async (orgID: string): Promise<Store[]> => {
    return await api
      .get<{ data: { stores: Store[] } }>(URLs.STORES(orgID))
      .then((response) => response?.data?.data?.stores)
  },
  byID: async (params: Stores): Promise<StoreData> => {
    return await api
      .get<StoreData>(URLs.Stores.byID(params?.orgID || '', params?.storeID || ''))
      .then((response) => response.data)
  },
  delete: async (payload: Partial<Stores>): Promise<Stores> => {
    return api
      .delete<Stores>(URLs.Stores.byID(payload?.orgID || '', payload?.storeID || ''))
      .then((response) => response.data)
  },
  list: async (params?: StoreListParams): Promise<Stores[]> => {
    const { orgID = '', ...queryParams } = params || {}
    const query = buildQueryParams(queryParams)
    return await api
      .get<ApiResponse<StoresData>>(URLs.Stores.list(orgID, query))
      .then((response) => response.data?.data?.stores || [])
  },
  listCursor: async (
    params: StoreListParams,
  ): Promise<CursorPaginatedResponse<Partial<Stores>>> => {
    const { orgID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    const response = await api
      .get<ApiResponse<StoresData>>(URLs.Stores.list(orgID ?? '', query))
      .then((response) => response?.data?.data)

    return {
      data: response.stores,
      pageInfo: {
        first: response.first,
        prev: response.prev,
        cursor: response.cursor,
        next: response.next,
        last: response.last,
      },
    }
  },
  update: async (payload: Partial<Stores>) => {
    const { orgID, ...patchData } = payload
    return await api
      .patch(URLs.Stores.byID(orgID || '', payload.storeID || ''), patchData)
      .then((response) => response.data)
  },
}

export const storesApi: typeof storesApiReal = IS_MOCK_MODE ? storesApiMock : storesApiReal
